import { getDatabase, ref, push } from "firebase/database";

/**
 * Firebase Realtime Database only (no browser console).
 * Path: client_fail_logs/player_img_presign/{dd-mm-yy}/{auctionId}/{autoGeneratedPushKey}
 */
export const FIREBASE_PLAYER_IMG_FAIL_ROOT = "client_fail_logs/player_img_presign";

/** Local calendar date for grouping logs (dd-mm-yy). */
export function formatPlayerImgFailLogDate(d = new Date()) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}-${mm}-${yy}`;
}

function persistPlayerImgFailureToFirebase(payload) {
  let aidKey = payload?.aid;
  if (aidKey === undefined || aidKey === null || aidKey === "") {
    aidKey = "_no_auction";
  }
  const dateKey = formatPlayerImgFailLogDate();
  let safePayload;
  try {
    safePayload = JSON.parse(JSON.stringify(payload));
  } catch {
    safePayload = {
      outcome: "FAIL",
      where: "persistPlayerImgFailureToFirebase",
      reason: "Could not serialize payload for Firebase",
      at: new Date().toISOString(),
    };
  }
  try {
    const db = getDatabase();
    const logsRef = ref(
      db,
      `${FIREBASE_PLAYER_IMG_FAIL_ROOT}/${dateKey}/${String(aidKey)}`
    );
    push(logsRef, safePayload).catch(() => {});
  } catch {
    /* silent — no console */
  }
}

/** Presigned URLs include auth in the query — log host/path summary only. */
export function summarizePresignedUrl(url) {
  if (!url || typeof url !== "string") {
    return { present: false };
  }
  try {
    const u = new URL(url);
    return {
      present: true,
      host: u.host,
      methodHint: "PUT",
      pathnameChars: u.pathname.length,
      queryParamKeys: [...new Set([...u.searchParams.keys()])],
    };
  } catch {
    return { present: true, urlParseFailed: true, lengthChars: url.length };
  }
}

export function getCroppedImageSourceInfo(croppedImage) {
  if (!croppedImage || typeof croppedImage !== "string") {
    return { kind: "empty" };
  }
  if (croppedImage.startsWith("data:")) {
    const mimeMatch = /^data:([^;,]+)/.exec(croppedImage);
    const comma = croppedImage.indexOf(",");
    return {
      kind: "data-url",
      mime: mimeMatch ? mimeMatch[1] : "unknown",
      approxBase64Chars: comma >= 0 ? croppedImage.length - comma - 1 : 0,
    };
  }
  if (croppedImage.startsWith("blob:")) {
    return { kind: "blob-url" };
  }
  if (croppedImage.startsWith("http://") || croppedImage.startsWith("https://")) {
    return { kind: "http(s)-url" };
  }
  return { kind: "other", sample: croppedImage.slice(0, 40) };
}

/** Intentionally no console / no Firebase — kept for call sites (flow markers). */
export function logPlayerImgUpload(_step, _payload) {}

/** Writes one row to Firebase only (no console). */
export function logPlayerImgFailure(where, reason, details = {}) {
  const payload = {
    outcome: "FAIL",
    where,
    reason: reason || "(no reason string)",
    at: new Date().toISOString(),
    ...details,
  };
  persistPlayerImgFailureToFirebase(payload);
}

export function pickApiMessage(data) {
  if (data == null || typeof data !== "object") return undefined;
  return (
    data.msg ??
    data.message ??
    data.error ??
    data.err ??
    (typeof data.data === "string" ? data.data : undefined)
  );
}

export function redactPresignedInApiPayload(payload) {
  if (payload == null || typeof payload !== "object") return payload;
  try {
    const copy = JSON.parse(JSON.stringify(payload));
    if (copy.data?.presignedUrl) {
      copy.data.presignedUrl = summarizePresignedUrl(copy.data.presignedUrl);
    }
    return copy;
  } catch {
    return { note: "could not clone api payload for log" };
  }
}

/** Presign storage name: {aid}_{pcode}_{name} e.g. 56529_23_deepak */
export function buildPlayerImgSname(auctionId, pcode, pname) {
  const aidPart = String(auctionId ?? "").trim();
  const pcodePart = String(pcode ?? "").trim();
  const namePart = String(pname ?? "")
    .trim()
    .split(/\s+/)[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (!aidPart || !pcodePart || !namePart) return "";
  return `${aidPart}_${pcodePart}_${namePart}`;
}

/** Join flow presign storage name: {aid}_{5randomDigits}_{name} e.g. 56529_48291_deepak */
export function buildJoinPlayerImgSname(auctionId, _mobnoIgnored, pname) {
  const aidPart = String(auctionId ?? "").trim();
  const numberPart = String(Math.floor(Math.random() * 100000)).padStart(5, "0");
  const namePart = String(pname ?? "")
    .trim()
    .split(/\s+/)[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (!aidPart || !namePart) return "";
  return `${aidPart}_${numberPart}_${namePart}`;
}

function sanitizeSnamePart(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

/** Team add presign storage name: {aid}_{teamname} */
export function buildTeamAddImgSname(auctionId, teamName) {
  const aidPart = String(auctionId ?? "").trim();
  const namePart = sanitizeSnamePart(teamName);
  if (!aidPart || !namePart) return "";
  return `${aidPart}_${namePart}`;
}

/** Team edit presign storage name: {aid}_{tmid}_{teamname} */
export function buildTeamEditImgSname(auctionId, tmid, teamName) {
  const aidPart = String(auctionId ?? "").trim();
  const tmidPart = String(tmid ?? "").trim();
  const namePart = sanitizeSnamePart(teamName);
  if (!aidPart || !tmidPart || !namePart) return "";
  return `${aidPart}_${tmidPart}_${namePart}`;
}
